import type { List } from 'immutable';
import type { DateTime } from 'luxon';

import type { scheduledTransactionsTypes } from 'app-components/flux/scheduled-transactions';
import type { RecurrenceType } from 'app-components/flux/scheduled-transactions/customFrequencyTypes';
import type {
  ScheduledTransaction,
  ScheduledTransactionType,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import {
  scheduledTransactionEditMatchCriteriaOptions,
  type ScheduledTransactionEditSeriesType,
  type ScheduledTransactionEditMatchOptions,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionEditForm';
import type { tagsTypes } from 'app-components/flux/tags';
import type { transactionsTypes } from 'app-components/flux/transactions';
import type { RootModalDrawerOptions } from 'components/Drawers/standardDrawerContract';

export interface ScheduledTransactionEditDialogProps {
  open: boolean;
  title?: string;
  scheduledTransaction: ScheduledTransaction | ScheduledTransactionType;
  onClose: (isDirty?: boolean, forceClose?: boolean) => void;
  onRootModalDrawerDirtyChange?: (isDirty: boolean) => void;
  onSubmit?: () => void;
  onBack?: () => void;
  rootModalDrawer?: RootModalDrawerOptions;
  subscriptionInitial?: boolean;
  incomeInitial?: boolean;
  transferInitial?: boolean;
  fakeCreate?: boolean;
  initialTab?: number;
  /** When true, open the "Assign Splits to Series" flow immediately. */
  initialShowAssignSplits?: boolean;
}

export const matchCriteriaOptions = scheduledTransactionEditMatchCriteriaOptions;
export type MatchOptions = ScheduledTransactionEditMatchOptions;

export interface ScheduledTransactionEditDialogFormValues
  extends Omit<scheduledTransactionsTypes.ScheduledTransactionType, 'dueOn' | 'transaction'> {
  dueOn: DateTime<true>;

  transaction: Omit<transactionsTypes.TransactionProps, 'tags' | 'accountId'> & {
    tags?: transactionsTypes.TransactionProps['tags'] | List<tagsTypes.TagProps>;
    excludeF2SDest?: boolean;
    excludeF2SUserOverridden?: boolean;
    excludeReportsDest?: boolean;
    accountId: string;
  };
  virtualFrequency: string;
  virtualEndOn: DateTime<boolean> | null | undefined;
  virtualRecurrence: RecurrenceType;
  matchCriteria: MatchOptions;
}

export type ScheduledTransactionEditFormAdapter = {
  values: ScheduledTransactionEditDialogFormValues;
  errors: Record<string, any>;
  touched: Record<string, any>;
  dirty: boolean;
  setFieldValue: (field: string, value: any) => unknown;
  setFieldError: (field: string, message: string | undefined) => void;
  setErrors: (errors: Record<string, any>) => void;
  setSubmitting?: (isSubmitting: boolean) => void;
};

export type ScheduledTransactionEditFormSetters = {
  setFieldValue: (field: string, value: any) => unknown;
  setDueOn: (value: DateTime<boolean> | undefined) => unknown;
  setVirtualEndOn: (value: DateTime<boolean> | null | undefined) => unknown;
  setVirtualFrequency: (value: string) => unknown;
  setVirtualRecurrence: (value: RecurrenceType) => unknown;
  setMatchCriteria: (value: MatchOptions) => unknown;
  setMatchAnyAmount: (value: boolean) => unknown;
  setMatchAmountVariance: (value: number) => unknown;
  setResetMatchAmountVariance: (value: boolean) => unknown;
  setAutoAdjustAmount: (value: scheduledTransactionsTypes.AutoAdjustAmount) => unknown;
  setAutoAdjustDueOn: (value: boolean) => unknown;
  setPresentmentAccountId: (value: string | null | undefined) => unknown;
  setBillerEnrollmentStatus: (value: string | null | undefined) => unknown;
  setTransaction: (value: ScheduledTransactionEditDialogFormValues['transaction']) => unknown;
  setTransactionPayee: (value: string) => unknown;
  setTransactionAmount: (value: number | '') => unknown;
  setTransactionAccountId: (value: string) => unknown;
  setTransactionCoa: (value: ScheduledTransactionEditDialogFormValues['transaction']['coa']) => unknown;
  setTransactionTags: (value: ScheduledTransactionEditDialogFormValues['transaction']['tags']) => unknown;
  setTransactionBusinessId: (value: string | undefined) => unknown;
  setTransactionSplit: (value: ScheduledTransactionEditDialogFormValues['transaction']['split']) => unknown;
  setTransactionSplitItems: (
    value: ScheduledTransactionEditDialogFormValues['transaction']['split'] extends {
      items?: infer Items;
    }
      ? Items
      : unknown,
  ) => unknown;
  setTransactionIsExcludedFromF2S: (value: boolean) => unknown;
  setTransactionExcludeF2SDest: (value: boolean) => unknown;
  setTransactionExcludeF2SUserOverridden: (value: boolean) => unknown;
  setTransactionIsExcludedFromReports: (value: boolean) => unknown;
  setTransactionExcludeReportsDest: (value: boolean) => unknown;
  setTransactionIsBill: (value: boolean) => unknown;
  setTransactionIsSubscription: (value: boolean) => unknown;
  setTransactionIsTransfer: (value: boolean) => unknown;
  setTransactionIsIncome: (value: boolean) => unknown;
  setTransactionIsCredit: (value: boolean) => unknown;
  setSeriesType: (value: ScheduledTransactionEditSeriesType) => unknown;
};

export type ScheduledTransactionEditFormValidation = {
  errors: Record<string, any>;
  touched: Record<string, any>;
  setFieldError: (field: string, message: string | undefined) => void;
  setErrors: (errors: Record<string, any>) => void;
  setSubmitting?: (isSubmitting: boolean) => void;
};

export type ScheduledTransactionEditFormFields = {
  values: ScheduledTransactionEditDialogFormValues;
  setters: ScheduledTransactionEditFormSetters;
  derived: {
    dirty: boolean;
    transaction: ScheduledTransactionEditDialogFormValues['transaction'];
  };
};

export type ScheduledTransactionEditFormAccessors = ScheduledTransactionEditFormFields & {
  validation: ScheduledTransactionEditFormValidation;
};

export const scheduledTransactionEditSetterPaths = {
  setDueOn: 'dueOn',
  setVirtualEndOn: 'virtualEndOn',
  setVirtualFrequency: 'virtualFrequency',
  setVirtualRecurrence: 'virtualRecurrence',
  setMatchCriteria: 'matchCriteria',
  setMatchAnyAmount: 'matchAnyAmount',
  setMatchAmountVariance: 'matchAmountVariance',
  setResetMatchAmountVariance: 'resetMatchAmountVariance',
  setAutoAdjustAmount: 'autoAdjustAmount',
  setAutoAdjustDueOn: 'autoAdjustDueOn',
  setPresentmentAccountId: 'presentmentAccountId',
  setBillerEnrollmentStatus: 'billerEnrollmentStatus',
  setTransaction: 'transaction',
  setTransactionPayee: 'transaction.payee',
  setTransactionAmount: 'transaction.amount',
  setTransactionAccountId: 'transaction.accountId',
  setTransactionCoa: 'transaction.coa',
  setTransactionTags: 'transaction.tags',
  setTransactionBusinessId: 'transaction.businessId',
  setTransactionSplit: 'transaction.split',
  setTransactionSplitItems: 'transaction.split.items',
  setTransactionIsExcludedFromF2S: 'transaction.isExcludedFromF2S',
  setTransactionExcludeF2SDest: 'transaction.excludeF2SDest',
  setTransactionExcludeF2SUserOverridden: 'transaction.excludeF2SUserOverridden',
  setTransactionIsExcludedFromReports: 'transaction.isExcludedFromReports',
  setTransactionExcludeReportsDest: 'transaction.excludeReportsDest',
  setTransactionIsBill: 'transaction.isBill',
  setTransactionIsSubscription: 'transaction.isSubscription',
  setTransactionIsTransfer: 'transaction.isTransfer',
  setTransactionIsIncome: 'transaction.isIncome',
  setTransactionIsCredit: 'transaction.isCredit',
} as const satisfies Record<
  Exclude<keyof ScheduledTransactionEditFormSetters, 'setFieldValue' | 'setSeriesType'>,
  string
>;

export const buildScheduledTransactionEditFormSetters = (
  setFieldValue: ScheduledTransactionEditFormSetters['setFieldValue'],
  setSeriesType: ScheduledTransactionEditFormSetters['setSeriesType'] = () => undefined,
): ScheduledTransactionEditFormSetters => ({
  setFieldValue,
  setSeriesType,
  setDueOn: (value) => setFieldValue(scheduledTransactionEditSetterPaths.setDueOn, value),
  setVirtualEndOn: (value) => setFieldValue(scheduledTransactionEditSetterPaths.setVirtualEndOn, value),
  setVirtualFrequency: (value) => setFieldValue(scheduledTransactionEditSetterPaths.setVirtualFrequency, value),
  setVirtualRecurrence: (value) => setFieldValue(scheduledTransactionEditSetterPaths.setVirtualRecurrence, value),
  setMatchCriteria: (value) => setFieldValue(scheduledTransactionEditSetterPaths.setMatchCriteria, value),
  setMatchAnyAmount: (value) => setFieldValue(scheduledTransactionEditSetterPaths.setMatchAnyAmount, value),
  setMatchAmountVariance: (value) => setFieldValue(scheduledTransactionEditSetterPaths.setMatchAmountVariance, value),
  setResetMatchAmountVariance: (value) =>
    setFieldValue(scheduledTransactionEditSetterPaths.setResetMatchAmountVariance, value),
  setAutoAdjustAmount: (value) => setFieldValue(scheduledTransactionEditSetterPaths.setAutoAdjustAmount, value),
  setAutoAdjustDueOn: (value) => setFieldValue(scheduledTransactionEditSetterPaths.setAutoAdjustDueOn, value),
  setPresentmentAccountId: (value) => setFieldValue(scheduledTransactionEditSetterPaths.setPresentmentAccountId, value),
  setBillerEnrollmentStatus: (value) =>
    setFieldValue(scheduledTransactionEditSetterPaths.setBillerEnrollmentStatus, value),
  setTransaction: (value) => setFieldValue(scheduledTransactionEditSetterPaths.setTransaction, value),
  setTransactionPayee: (value) => setFieldValue(scheduledTransactionEditSetterPaths.setTransactionPayee, value),
  setTransactionAmount: (value) => setFieldValue(scheduledTransactionEditSetterPaths.setTransactionAmount, value),
  setTransactionAccountId: (value) => setFieldValue(scheduledTransactionEditSetterPaths.setTransactionAccountId, value),
  setTransactionCoa: (value) => setFieldValue(scheduledTransactionEditSetterPaths.setTransactionCoa, value),
  setTransactionTags: (value) => setFieldValue(scheduledTransactionEditSetterPaths.setTransactionTags, value),
  setTransactionBusinessId: (value) =>
    setFieldValue(scheduledTransactionEditSetterPaths.setTransactionBusinessId, value),
  setTransactionSplit: (value) => setFieldValue(scheduledTransactionEditSetterPaths.setTransactionSplit, value),
  setTransactionSplitItems: (value) =>
    setFieldValue(scheduledTransactionEditSetterPaths.setTransactionSplitItems, value),
  setTransactionIsExcludedFromF2S: (value) =>
    setFieldValue(scheduledTransactionEditSetterPaths.setTransactionIsExcludedFromF2S, value),
  setTransactionExcludeF2SDest: (value) =>
    setFieldValue(scheduledTransactionEditSetterPaths.setTransactionExcludeF2SDest, value),
  setTransactionExcludeF2SUserOverridden: (value) =>
    setFieldValue(scheduledTransactionEditSetterPaths.setTransactionExcludeF2SUserOverridden, value),
  setTransactionIsExcludedFromReports: (value) =>
    setFieldValue(scheduledTransactionEditSetterPaths.setTransactionIsExcludedFromReports, value),
  setTransactionExcludeReportsDest: (value) =>
    setFieldValue(scheduledTransactionEditSetterPaths.setTransactionExcludeReportsDest, value),
  setTransactionIsBill: (value) => setFieldValue(scheduledTransactionEditSetterPaths.setTransactionIsBill, value),
  setTransactionIsSubscription: (value) =>
    setFieldValue(scheduledTransactionEditSetterPaths.setTransactionIsSubscription, value),
  setTransactionIsTransfer: (value) =>
    setFieldValue(scheduledTransactionEditSetterPaths.setTransactionIsTransfer, value),
  setTransactionIsIncome: (value) => setFieldValue(scheduledTransactionEditSetterPaths.setTransactionIsIncome, value),
  setTransactionIsCredit: (value) => setFieldValue(scheduledTransactionEditSetterPaths.setTransactionIsCredit, value),
});

export const toScheduledTransactionEditFormAccessors = (
  formProps: ScheduledTransactionEditFormAdapter,
): ScheduledTransactionEditFormAccessors => {
  const { setFieldValue } = formProps;
  return {
    values: formProps.values,
    setters: buildScheduledTransactionEditFormSetters(setFieldValue),
    validation: {
      errors: formProps.errors,
      touched: formProps.touched,
      setFieldError: formProps.setFieldError,
      setErrors: formProps.setErrors,
      setSubmitting: formProps.setSubmitting,
    },
    derived: {
      dirty: formProps.dirty,
      transaction: formProps.values.transaction,
    },
  };
};
